import moment from "moment-timezone";

import { TIMEZONE } from "../term-contract/helpers";

export const VALIDITY_MONTHS = 12;
export const FULL_LIST_HELP = "Items missing from this file stop matching.";
export const LINKED_BY = { sku: "by SKU", eth: "by ETH account", template: "by template", manual: "by hand" };
export const IMPORT_ACCEPT = ".xlsx,.csv,.pdf"; // eezee-server IMPORT_TYPES
export const PREVIEW_PAGE_SIZE = 100;

/* the template's columns (eezee-server supplier-price-list-xlsx.service) */
export const XLSX_REQUIRED = ["title", "costPrice and/or listPrice"];
export const XLSX_OPTIONAL = ["supplierCode", "brand", "model", "spec", "uom", "moq", "tier1MinQty / tier1CostPrice / tier1ListPrice … tier3*", "remarks", "sku"];
export const XLSX_DERIVED = ["productId", "productTitle", "linkedBy"];

/** "YYYY-MM-DD" twelve months after `date` (a moment or a "YYYY-MM-DD"), the default validTo of a file. */
export const defaultValidTo = ({
    date,
}) => date ? moment.tz(date, TIMEZONE).add(VALIDITY_MONTHS, "months").format("YYYY-MM-DD") : null;

export const formatDate = ({
    date,
}) => date ? moment.tz(date, TIMEZONE).format("DD MMM YYYY") : "—";

/** A price-history entry's upload: ref → original file name → template file name → "Upload", with its file date. */
export const uploadLabel = ({
    upload,
}) => `${upload.sourceRef || (upload.sourceFile && upload.sourceFile.name) || (upload.templateFile && upload.templateFile.name) || "Upload"} (file date ${formatDate({ date: upload.fileDate })})`;

/** "$1.275", "$1.70": 2 to 3 decimals, half up on the decimal as written (toFixed alone shows 1.275 as 1.27, its binary value). */
export const money = ({
    value,
}) => {
    if (value === null || value === undefined) return "—";
    const rounded = Math.round(`${Number(value)}e3`) / 1000;
    // ponytail: "1e-7e3" is NaN, so exponent-notation values skip the decimal rounding and fall back to toFixed.
    return `$${(Number.isFinite(rounded) ? rounded : Number(value)).toFixed(3).replace(/0$/, "")}`;
};

/** "15 Jul 2026, $3.71 → $4.20" from the last two priceHistory entries (cost when both have one, else list); null when it never changed. */
export const lastPriceChange = ({
    line,
}) => {
    const history = line.priceHistory || [];
    if (history.length < 2) return null;
    const [before, after] = history.slice(-2);
    const field = before.costPrice !== null && before.costPrice !== undefined && after.costPrice !== null && after.costPrice !== undefined
        ? "costPrice" : "listPrice";
    return `${formatDate({ date: after.fileDate })}, ${money({ value: before[field] })} → ${money({ value: after[field] })}`;
};

/** "≥10: $4.00 / ≥50: $3.80" (cost, else list), or "" */
export const formatTiers = ({
    tiers,
}) => (tiers || []).map(t => `≥${t.minQty}: ${money({ value: t.costPrice !== null && t.costPrice !== undefined ? t.costPrice : t.listPrice })}`).join(" / ");

/** The preview's "file / sheet / row" label of a parsed row or warning. */
export const sourceLabel = ({
    item,
}) => [item.sourceFile, item.sourceSheet, item.sourceRow !== undefined && item.sourceRow !== null ? `row ${item.sourceRow}` : null]
    .filter(Boolean).join(" / ");

/** A parse warning as one line of text. */
export const warningText = ({
    warning,
}) => {
    const where = sourceLabel({ item: warning });
    return where ? `${where}: ${warning.message}` : warning.message;
};

/**
 * "file / AET: supplierCode = A, title = B, listPrice = F; brand = AET" per mapped sheet; a recognised template is named,
 * and a PDF (no sheets) says its rows were read by the model.
 */
export const mappingLines = ({
    mappings,
}) => (mappings || []).reduce((lines, m) => {
    const head = m.template ? [`${m.sourceFile}: read as the ${m.template} template`]
        : m.sheets ? [] : [`${m.sourceFile}: rows read by the model`];
    return [...lines, ...head, ...(m.sheets || []).map(sheet => {
        if (sheet.skip) return `${m.sourceFile} / ${sheet.sheet}: skipped${sheet.skipReason ? ` (${sheet.skipReason})` : ""}`;
        const columns = (sheet.blocks || []).map(block => Object.keys(block)
            .filter(field => block[field] && !Array.isArray(block[field]))
            .map(field => `${field} = ${block[field]}`)
            .join(", "));
        const constants = [sheet.brand && `brand = ${sheet.brand}`, sheet.uom && `uom = ${sheet.uom}`].filter(Boolean);
        return `${m.sourceFile} / ${sheet.sheet}: ${[...columns, ...constants].join("; ")}`;
    })];
}, []);

/** Key of the warnings that point at a row, for "rows with warnings only". */
export const rowSourceKey = ({
    item,
}) => `${item.sourceFile}|${item.sourceSheet}|${item.sourceRow}`;

/** The preview's rows as the commit body wants them: the client-only `_` fields stripped. */
export const rowsToCommit = ({
    rows,
}) => rows.map(row => Object.keys(row).filter(k => k[0] !== "_").reduce((out, k) => ({ ...out, [k]: row[k] }), {}));



// WEBPACK FOOTER //
// ./src/components/supplier-price-list/helpers.js